import { useEffect } from 'react';

/**
 * Per-route head manager. Replaces the static title/description/canonical
 * from index.html with values specific to the current page, so Google sees
 * (and indexes) a distinct snippet per URL instead of every page sharing
 * the homepage canonical.
 *
 * Usage: drop <SEO title=... description=... path=... /> near the top of
 * each page. For non-public routes (wizard, admin), pass noindex.
 */

type Props = {
  title: string;
  description?: string;
  // Path portion of the canonical URL, e.g. "/services". Omit for pages
  // where no canonical should be set (dynamic/private routes).
  path?: string;
  noindex?: boolean;
};

const SITE = 'https://dormantaccountsfiling.com';

function upsertMeta(selector: string, create: () => HTMLElement) {
  let el = document.head.querySelector<HTMLElement>(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
}

function setMetaName(name: string, content: string) {
  const el = upsertMeta(`meta[name="${name}"]`, () => {
    const m = document.createElement('meta');
    m.setAttribute('name', name);
    return m;
  });
  el.setAttribute('content', content);
}

function setMetaProperty(property: string, content: string) {
  const el = upsertMeta(`meta[property="${property}"]`, () => {
    const m = document.createElement('meta');
    m.setAttribute('property', property);
    return m;
  });
  el.setAttribute('content', content);
}

function setCanonical(href: string) {
  const el = upsertMeta('link[rel="canonical"]', () => {
    const l = document.createElement('link');
    l.setAttribute('rel', 'canonical');
    return l;
  });
  el.setAttribute('href', href);
}

export function SEO({ title, description, path, noindex }: Props) {
  useEffect(() => {
    document.title = title;

    if (description) {
      setMetaName('description', description);
      setMetaProperty('og:description', description);
      setMetaName('twitter:description', description);
    }

    setMetaProperty('og:title', title);
    setMetaName('twitter:title', title);

    if (noindex) {
      setMetaName('robots', 'noindex,nofollow');
    } else {
      // Public pages: ensure any prior noindex is cleared.
      setMetaName('robots', 'index,follow');
    }

    if (path) {
      const canonical = `${SITE}${path}`;
      setCanonical(canonical);
      setMetaProperty('og:url', canonical);
    }
  }, [title, description, path, noindex]);

  return null;
}
